import React, { useEffect, useRef, useState } from 'react';
import AvatarModel from './AvatarModel.jsx';

const STATE_LABELS = Object.freeze({
  idle: 'Ready',
  listening: 'Listening',
  speaking: 'Speaking',
  thinking: 'Thinking',
  guiding: 'Guiding',
});

const icon = (text, label) => <span aria-hidden="true" title={label}>{text}</span>;
const DRAG_THRESHOLD = 4;
const VIEWPORT_GUTTER = 8;

const clamp = (value, minimum, maximum) => Math.min(maximum, Math.max(minimum, value));

export function AvatarPanel({
  avatar,
  state = 'idle',
  dock,
  position = { x: 0, y: 0 },
  muted,
  activeSpeaker,
  reducedMotion,
  onAsk,
  onVoiceToggle,
  onMuteToggle,
  onMinimize,
  onHide,
  onPositionChange,
}) {
  const [controlsOpen, setControlsOpen] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [localPosition, setLocalPosition] = useState(position);
  const drag = useRef(null);

  const listening = state === 'listening';
  const speaking = state === 'speaking';

  const posX = position.x;
  const posY = position.y;
  useEffect(() => {
    if (!drag.current) setLocalPosition({ x: posX, y: posY });
  }, [posX, posY]);

  const handlePointerDown = (event) => {
    if (event.button !== 0) return;
    const rect = event.currentTarget.closest('.cl-avatar')?.getBoundingClientRect();
    if (!rect) return;

    event.currentTarget.setPointerCapture?.(event.pointerId);
    drag.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      origin: localPosition,
      moved: false,
      minX: localPosition.x + VIEWPORT_GUTTER - rect.left,
      maxX: localPosition.x + window.innerWidth - VIEWPORT_GUTTER - rect.right,
      minY: localPosition.y + VIEWPORT_GUTTER - rect.top,
      maxY: localPosition.y + window.innerHeight - VIEWPORT_GUTTER - rect.bottom,
    };
    setDragging(true);
  };

  const handlePointerMove = (event) => {
    const current = drag.current;
    if (!current || current.pointerId !== event.pointerId) return;
    const deltaX = event.clientX - current.startX;
    const deltaY = event.clientY - current.startY;
    if (!current.moved && Math.hypot(deltaX, deltaY) >= DRAG_THRESHOLD) current.moved = true;
    if (!current.moved) return;

    event.preventDefault();
    const nextPosition = {
      x: clamp(current.origin.x + deltaX, current.minX, current.maxX),
      y: clamp(current.origin.y + deltaY, current.minY, current.maxY),
    };
    current.position = nextPosition;
    setLocalPosition(nextPosition);
  };

  const finishPointer = (event) => {
    const current = drag.current;
    if (!current || current.pointerId !== event.pointerId) return;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
    drag.current = null;
    setDragging(false);
    if (current.moved) onPositionChange?.(avatar.id, current.position || localPosition);
    else setControlsOpen((open) => !open);
  };

  const handleKeyDown = (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    setControlsOpen((open) => !open);
  };

  const resetPosition = () => {
    const reset = { x: 0, y: 0 };
    setLocalPosition(reset);
    onPositionChange?.(avatar.id, reset);
  };

  return (
    <section
      className={`cl-avatar cl-avatar--${dock} cl-avatar--${state}${activeSpeaker ? ' cl-avatar--active-speaker' : ''}${dragging ? ' is-dragging' : ''}${controlsOpen ? ' has-open-controls' : ''}`}
      style={{
        '--avatar-accent': avatar.accent || '#31d7ff',
        '--avatar-x': `${localPosition.x}px`,
        '--avatar-y': `${localPosition.y}px`,
      }}
      aria-label={`${avatar.name} avatar`}
      data-avatar-id={avatar.id}
      data-state={state}
    >
      <div
        className="cl-avatar__figure"
        role="button"
        tabIndex="0"
        aria-label={`${avatar.name}, ${STATE_LABELS[state] || STATE_LABELS.idle}. Drag to move; click for controls.`}
        aria-expanded={controlsOpen}
        title={`Drag ${avatar.name} to move. Click for controls.`}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={finishPointer}
        onPointerCancel={finishPointer}
        onKeyDown={handleKeyDown}
      >
        <AvatarModel
          avatar={avatar}
          state={state}
          active
          reducedMotion={reducedMotion}
        />
        <span className="cl-avatar__ground" aria-hidden="true" />
        <span className="cl-avatar__presence" aria-hidden="true" />
      </div>

      {controlsOpen ? (
        <div className="cl-avatar__popover" role="group" aria-label={`${avatar.name} controls`}>
          <div className="cl-avatar__identity">
            <div>
              <strong>{avatar.name}</strong>
              <span>{avatar.role}</span>
            </div>
            <span className="cl-avatar__state" aria-live="polite">
              {STATE_LABELS[state] || STATE_LABELS.idle}
            </span>
            <button
              type="button"
              className="cl-avatar__close"
              onClick={() => setControlsOpen(false)}
              aria-label={`Close ${avatar.name} controls`}
            >
              &times;
            </button>
          </div>

          <div className="cl-avatar__primary-actions">
            <button type="button" className="cl-avatar__ask" onClick={() => onAsk?.(avatar.id)}>
              Ask {avatar.name}
            </button>
            <button
              type="button"
              className={`cl-avatar__icon-button${listening ? ' is-active' : ''}`}
              aria-label={listening ? `Stop ${avatar.name} listening` : `Talk to ${avatar.name}`}
              aria-pressed={listening}
              onClick={() => onVoiceToggle?.(avatar.id, !listening)}
            >
              {icon('\u25C9', 'Voice')}
            </button>
          </div>

          <div className="cl-avatar__utility-actions">
            <button type="button" onClick={() => onMuteToggle?.(avatar.id)} aria-pressed={muted}>
              {muted ? 'Unmute' : speaking ? 'Mute now' : 'Mute'}
            </button>
            <button type="button" onClick={resetPosition}>Reset position</button>
            <button type="button" onClick={() => onMinimize?.(avatar.id)}>Minimize</button>
            <button type="button" onClick={() => onHide?.(avatar.id)}>Hide</button>
          </div>
        </div>
      ) : null}
    </section>
  );
}

export default AvatarPanel;
